import { useState, useRef } from 'react';
import { Link } from 'react-router';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { UserType } from '../hooks/useCurrentUser';

export interface UserMenuProps {
    /** The full display name of the current user, or null if not available. */
    displayName: string | null;
    /** The email address of the current user. */
    email: string | null;
    /** The role of the current user. */
    userType: UserType | null;
    /** Callback invoked when the user clicks Sign Out. */
    onSignOut: () => void;
}

/**
 * Derives initials from a display name.
 * Uses first letter of first word and first letter of last word.
 * Returns "?" if displayName is null or empty.
 */
function getInitials(displayName: string | null): string {
    if (!displayName || displayName.trim() === '') return '?';
    const parts = displayName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Derives a deterministic background color from a string for the avatar.
 */
function getAvatarColor(seed: string | null): string {
    const colors = [
        '#2563eb', // blue
        '#7c3aed', // violet
        '#db2777', // pink
        '#059669', // emerald
        '#d97706', // amber
        '#0891b2', // cyan
        '#dc2626', // red
        '#65a30d', // lime
    ];
    if (!seed) return colors[0];
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
}

/**
 * UserMenu component — renders a circular avatar with user initials.
 * Clicking the avatar opens a MUI Menu with user info and a Sign Out item.
 * The theme toggle that used to live here moved to /settings → Appearance
 * (#1825) so admins clicking 'Settings' find it where they expect.
 * The menu closes on outside click, item click, or Escape key (handled by MUI Menu).
 */
function UserMenu({ displayName, email, userType, onSignOut }: UserMenuProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const containerRef = useRef<HTMLDivElement>(null);

    const initials = getInitials(displayName);
    const avatarColor = getAvatarColor(displayName);

    const handleAvatarClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const roleLabel = userType === 'admin' ? 'Admin' : userType === 'signer' ? 'Signer' : null;

    return (
        <div className="user-avatar-menu" ref={containerRef} data-testid="user-menu">
            <button
                className="user-avatar-btn"
                aria-label="User menu"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={handleAvatarClick}
                style={{ backgroundColor: avatarColor }}
            >
                {initials}
            </button>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        className: 'user-avatar-dropdown',
                        sx: {
                            minWidth: 220,
                            mt: 0.5,
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* User info — not a clickable item */}
                <Box className="user-menu-info" sx={{ px: 2, py: 1.5, outline: 'none' }}>
                    <Typography className="user-menu-name" variant="body2" sx={{ fontWeight: 600 }}>
                        {displayName ?? 'User'}
                    </Typography>
                    {email && (
                        <Typography className="user-menu-email" variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            {email}
                        </Typography>
                    )}
                    {roleLabel && (
                        <Box
                            component="span"
                            className="user-menu-role-badge"
                            sx={{
                                display: 'inline-block',
                                mt: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                fontSize: '0.6875rem',
                                fontWeight: 600,
                                bgcolor: 'var(--color-sidebar-active-bg, #134e4a)',
                                color: 'var(--color-sidebar-active-text, #ccfbf1)',
                            }}
                        >
                            {roleLabel}
                        </Box>
                    )}
                </Box>

                <Divider className="user-menu-divider" />

                {/*
                 * Personal Settings — every signed-in user (admin + signer)
                 * lands here from the avatar menu (issue #2114). The shell
                 * itself has no admin gate, so this entry is shown to all
                 * users. Routes to `/account/profile`; the AccountSettings
                 * shell renders the four personal panels (Appearance,
                 * Profile, Notifications, Security).
                 */}
                <MenuItem
                    className="user-menu-item user-menu-personal-settings"
                    component={Link}
                    to="/account/profile"
                    onClick={handleClose}
                    sx={{ fontSize: '0.9375rem' }}
                >
                    Personal Settings
                </MenuItem>

                {/* Sign Out */}
                <MenuItem
                    className="user-menu-item user-menu-signout"
                    onClick={() => {
                        handleClose();
                        onSignOut();
                    }}
                    sx={{ fontSize: '0.9375rem' }}
                >
                    Sign Out
                </MenuItem>
            </Menu>
        </div>
    );
}

export default UserMenu;
